import { memo, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import {
  FaIdBadge,
  FaUserCheck,
  FaEnvelope,
  FaPhone,
  FaLanguage,
  FaVenusMars,
  FaBirthdayCake,
  FaGraduationCap,
  FaBook,
  FaLightbulb,
  FaChalkboardTeacher,
  FaHandsHelping,
  FaUsers,
  FaLayerGroup,
  FaTicketAlt,
} from "react-icons/fa";
import { User } from "../../types/Types";

interface YouthProfileProps {
  userData: User;
}

const YouthProfile = memo(({ userData }: YouthProfileProps) => {
  const formattedDateOfBirth = useMemo(
    () =>
      userData.dateOfBirth
        ? new Date(userData.dateOfBirth).toLocaleDateString()
        : "Not Provided Yet",
    [userData.dateOfBirth]
  );

  return (
    <div>
      {/* About */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">About</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col className="mb-3">
            <div className="d-flex align-items-start">
              <div className="text-muted small">
                {userData.aboutYou || "Not Provided Yet"}
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>

      {/* Personal Details */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">Personal Details</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaEnvelope size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Email</strong>
                <div className="text-muted small">
                  {userData.email || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaPhone size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Contact Number</strong>
                <div className="text-muted small">
                  {userData.phoneNumber || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaLanguage size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Language Preference</strong>
                <div className="text-muted small">
                  {userData.languagePreference || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaVenusMars size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Gender</strong>
                <div className="text-muted small">
                  {userData.gender || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBirthdayCake size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Date of Birth</strong>
                <div className="text-muted small">
                  {formattedDateOfBirth}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>

      {/* Education */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">Education</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaGraduationCap size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Education Level</strong>
                <div className="text-muted small">
                  {userData.educationLevel || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBook size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Field of Study or Interest</strong>
                <div className="text-muted small">
                  {userData.studyInterest || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>

      {/* Interests */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">Interests</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaLightbulb size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Mentorship Objectives</strong>
                <div className="text-muted small">
                  {userData.mentorshipObjectives || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaChalkboardTeacher
                size={20}
                className="me-2 mt-1 text-secondary"
              />
              <div>
                <strong>Preferred Mentorship Expertise</strong>
                <div className="text-muted small">
                  {userData.mentorshipExpertise || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaHandsHelping size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Preferred Mentorship Format</strong>
                <div className="text-muted small">
                  {userData.mentorshipFormat || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaLayerGroup size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Areas of Interest on Platform</strong>
                <div className="text-muted small">
                  {userData.platformInterest || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaUsers size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Interested in Peer Networking</strong>
                <div className="text-muted small">
                  {userData.peerNetworking || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>
      {/* Membership Details */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">Membership Details</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaIdBadge size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Membership Name</strong>
                <div className="text-muted small">
                  {userData.membershipName || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaUserCheck size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Membership Type</strong>
                <div className="text-muted small">
                  {userData.isAnnualMembership ? "Annual" : "Monthly"}
                </div>
              </div>
            </div>
          </Col>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaTicketAlt size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Referral Code</strong>
                <div className="text-muted small">
                  {userData.referralCode || "Not Provided Yet"}
                </div>
              </div>
            </div>
          </Col>
          {userData.referralCodeUsed && (
            <Col md={4} className="mb-3">
              <div className="d-flex align-items-start">
                <FaTicketAlt size={20} className="me-2 mt-1 text-secondary" />
                <div>
                  <strong>Referral Code Used</strong>
                  <div className="text-muted small">
                    {userData.referralCodeUsed || "Not Provided Yet"}
                  </div>
                </div>
              </div>
            </Col>
          )}
        </Row>
        </div>
      </div>
    </div>
  );
});
YouthProfile.displayName = "YouthProfile";

export default YouthProfile;
